import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tmpru from '../assets/img/tmpru.png';
import Trucky from '../assets/img/truckway.png';
import { FaArrowRight, FaExternalLinkAlt, FaHandshake, FaUsers } from 'react-icons/fa';
import useScrollAnimation from '../hooks/useScrollAnimation';

const mediaPartners = [
  {
    url: 'https://www.youtube.com/@truckwayets2',
    image: Trucky,
    name: 'TruckWay',
    channel: 'YouTube',
  },
  {
    url: 'https://vk.com/tmpru',
    image: Tmpru,
    name: 'TMP RU',
    channel: 'VK',
  },
];

const Partners = () => {
  const { t } = useTranslation();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const partnersRef = useScrollAnimation(0.12);

  useEffect(() => {
    const fetchPartners = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await fetch('https://hub.mileageriot.com/api/partners');
        if (!response.ok) throw new Error(`Partners request failed: ${response.status}`);

        const data = await response.json();
        const visiblePartners = Array.isArray(data)
          ? data.filter((partner) => {
              const searchable = `${partner?.name || ''} ${partner?.url || ''}`.toLowerCase();
              return !searchable.includes('manya');
            })
          : [];

        setPartners(visiblePartners);
      } catch (fetchError) {
        console.error('Error fetching partners:', fetchError);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  const hideBrokenLogo = (event) => {
    event.currentTarget.style.display = 'none';
  };

  return (
    <section className="partners-modern partners-upgraded" id="partners" ref={partnersRef}>
      <div className="partners-grid-background" aria-hidden="true"></div>
      <div className="partners-glow partners-glow-left" aria-hidden="true"></div>
      <div className="partners-glow partners-glow-right" aria-hidden="true"></div>

      <div className="container partners-container">
        <header className="partners-header fade-in-up">
          <div className="partners-header-copy">
            <div className="section-badge partners-section-badge">
              <FaHandshake aria-hidden="true" />
              <span>{t('partners.badge')}</span>
            </div>
            <h2>{t('partners.partners_title')}</h2>
            <p>{t('partners.subtitle')}</p>
          </div>

          <div className="partners-count" aria-label={t('partners.countLabel', { count: partners.length })}>
            <FaUsers aria-hidden="true" />
            <div>
              <strong>{loading ? '—' : partners.length}</strong>
              <span>{t('partners.inNetwork')}</span>
            </div>
          </div>
        </header>

        <div className="partners-subsection-heading fade-in-up stagger-2">
          <div>
            <span>{t('partners.mediaLabel')}</span>
            <h3>{t('partners.mediaTitle')}</h3>
          </div>
          <p>{t('partners.mediaSubtitle')}</p>
        </div>

        <div className="media-partners-grid fade-in-up stagger-2">
          {mediaPartners.map((partner, index) => (
            <a
              className="media-partner-card"
              href={partner.url}
              target="_blank"
              rel="noopener noreferrer"
              key={partner.name}
              aria-label={`${partner.name} — ${t('partners.visit')}`}
            >
              <div className="media-partner-index" aria-hidden="true">0{index + 1}</div>
              <div className="media-partner-logo-shell">
                <span className="partner-logo-fallback" aria-hidden="true">{partner.name.charAt(0)}</span>
                <img
                  loading="lazy"
                  decoding="async"
                  src={partner.image}
                  alt=""
                  onError={hideBrokenLogo}
                />
              </div>
              <div className="media-partner-copy">
                <span>{t('partners.mediaPartner')} · {partner.channel}</span>
                <strong>{partner.name}</strong>
              </div>
              <span className="partner-external-button" aria-hidden="true">
                <FaExternalLinkAlt />
              </span>
            </a>
          ))}
        </div>

        <div className="partners-subsection-heading partners-network-heading fade-in-up stagger-3">
          <div>
            <span>{t('partners.networkLabel')}</span>
            <h3>{t('partners.networkTitle')}</h3>
          </div>
          <p>{t('partners.networkSubtitle')}</p>
        </div>

        {loading ? (
          <div className="partners-logo-grid partners-loading-grid" role="status" aria-label={t('partners.loading')}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div className="partner-card-skeleton" key={index} aria-hidden="true">
                <span></span>
                <i></i>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="partners-state" role="alert">
            <FaHandshake aria-hidden="true" />
            <h3>{t('partners.error')}</h3>
            <p>{t('partners.errorHint')}</p>
          </div>
        ) : partners.length === 0 ? (
          <div className="partners-state">
            <FaHandshake aria-hidden="true" />
            <h3>{t('partners.empty')}</h3>
          </div>
        ) : (
          <div className="partners-logo-grid fade-in-up stagger-4">
            {partners.map((partner, index) => (
              <a
                className="partner-card-upgraded"
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                key={partner.id}
                aria-label={`${partner.name} — ${t('partners.visit')}`}
              >
                <div className="partner-card-top">
                  <span className="partner-card-index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <FaExternalLinkAlt aria-hidden="true" />
                </div>

                <div className="partner-card-logo-shell">
                  <span className="partner-logo-fallback" aria-hidden="true">
                    {partner.name?.replace(/^[^\p{L}\p{N}]+/u, '').charAt(0) || 'M'}
                  </span>
                  <img
                    loading="lazy"
                    decoding="async"
                    src={partner.logo}
                    alt=""
                    onError={hideBrokenLogo}
                  />
                </div>

                <div className="partner-card-copy">
                  <span>{t('partners.vtcPartner')}</span>
                  <strong title={partner.name}>{partner.name}</strong>
                </div>

                <div className="partner-card-footer" aria-hidden="true">
                  <span>{t('partners.open')}</span>
                  <FaArrowRight />
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Partners;
